import { useState, useEffect } from 'react';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import { TaskMetaSection } from './TaskMetaSection';
import { ReminderTimingPicker } from '../ReminderTimingPicker';
import { CommitmentPicker } from '../CommitmentPicker';

/**
 * Progressive-disclosure wrapper for the task form's power settings:
 * type, energy, recurrence, context batch, delegation, reminder and
 * commitment link. Collapsed by default on new tasks to keep the
 * create flow focused; auto-opens when editing a task that already
 * uses any of these.
 */
export function TaskAdvancedSection({ form, setForm, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { if (defaultOpen) setOpen(true); }, [defaultOpen]);

  const chips = [];
  if (form.taskType && form.taskType !== 'STANDARD') chips.push(form.taskType.charAt(0) + form.taskType.slice(1).toLowerCase());
  if (form.energy && form.energy !== 'MEDIUM') chips.push(`${form.energy.charAt(0)}${form.energy.slice(1).toLowerCase()} energy`);
  if (form.recurrenceRule && form.recurrenceRule !== 'NONE') chips.push(form.recurrenceRule.charAt(0) + form.recurrenceRule.slice(1).toLowerCase());
  if (form.context) chips.push(form.context.replace(/_/g, ' '));
  if (form.staffId || form.delegatedTo) chips.push('Delegated');
  if (form.reminderTiming && form.reminderTiming !== 'NONE') chips.push('Reminder');
  if (form.commitmentId) chips.push('Commitment');

  return (
    <div className="rounded-[18px]" style={{ border: '1px solid var(--pd-border)' }} data-testid="task-advanced-section">
      <button
        type="button"
        data-testid="task-advanced-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
      >
        <SlidersHorizontal className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--pd-subtle)' }} />
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--pd-subtle)' }}>More options</span>
        <div className="flex-1 flex items-center gap-1 flex-wrap justify-end min-w-0">
          {!open && chips.slice(0, 4).map((chip) => (
            <span key={chip} className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap" style={{ background: 'var(--pd-panel-2)', color: 'var(--pd-muted)' }}>
              {chip}
            </span>
          ))}
          {!open && chips.length > 4 && (
            <span className="text-[9px] font-semibold" style={{ color: 'var(--pd-subtle)' }}>+{chips.length - 4}</span>
          )}
        </div>
        <ChevronDown className="w-4 h-4 flex-shrink-0 transition-transform" style={{ color: 'var(--pd-subtle)', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 animate-fade-in" style={{ borderTop: '1px solid var(--pd-border)', paddingTop: '1rem' }} data-testid="task-advanced-body">
          <TaskMetaSection form={form} setForm={setForm} />
          <ReminderTimingPicker
            testId="task-reminder-timing"
            value={form.reminderTiming || 'NONE'}
            onChange={(value) => setForm((prev) => ({ ...prev, reminderTiming: value }))}
            helperText="Adds this task to the 8 AM UTC daily digest at the matching lead time."
          />
          <CommitmentPicker value={form.commitmentId} onChange={(id) => setForm((prev) => ({ ...prev, commitmentId: id }))} />
        </div>
      )}
    </div>
  );
}
